import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { movies, categories } from '@/mocks/data';
import SEOHead, { generateWebPageSchema } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';

type SortKey = 'rating' | 'year' | 'title';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'japanese-movie');
  const [sortBy, setSortBy] = useState<SortKey>('rating');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory],
  );

  const filteredMovies = useMemo(() => {
    const cat = categories.find((c) => c.slug === activeCategory);
    if (!cat) return [];

    let result = movies;
    if (cat.slug === 'japanese-movie' || cat.slug === 'korean-movie') {
      result = movies.filter((m) => m.region === cat.slug);
    } else {
      result = movies.filter((m) => m.category === cat.slug);
    }

    return [...result].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'year') return b.year - a.year;
      return a.title.localeCompare(b.title, 'zh');
    });
  }, [activeCategory, sortBy]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'rating', label: '评分最高' },
    { key: 'year', label: '最新上映' },
    { key: 'title', label: '名称排序' },
  ];

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: currentCategory?.name || '分类' },
  ];

  const schema = generateWebPageSchema(
    `${currentCategory?.name || '电影分类'} - 日韩影视网`,
    currentCategory?.description || '日韩电影分类浏览',
    `https://www.rihanyingshi.com/category/${activeCategory}`,
  );

  return (
    <>
      <SEOHead
        title={`${currentCategory?.name || '电影分类'} - 日韩影视网 | 免费日韩电影在线观看`}
        description={currentCategory?.description || '日韩电影分类浏览，发现更多精彩影片'}
        keywords={`日韩影视网,${currentCategory?.name || ''},日韩电影,免费电影`}
        canonicalUrl={`https://www.rihanyingshi.com/category/${activeCategory}`}
        schema={schema}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
              {currentCategory?.name || '电影分类'}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{currentCategory?.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveCategory(cat.slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-950 font-semibold'
                      : 'bg-background-800 text-foreground-400 hover:text-foreground-200 hover:bg-background-700'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1.5 bg-background-800 rounded-lg p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-600 text-foreground-100 font-medium'
                      : 'text-foreground-500 hover:text-foreground-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4 pb-16">
              {filteredMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  slug={movie.slug}
                  title={movie.title}
                  titleEn={movie.titleEn}
                  coverImage={movie.coverImage}
                  rating={movie.rating}
                  year={movie.year}
                  category={movie.category}
                  region={movie.region}
                  tags={movie.tags}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-800 text-foreground-500 mb-4">
                <i className="ri-movie-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无影片，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}